<template>
  <div class="fixed inset-0 z-[200] flex items-center justify-center">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black/50" @click="$emit('cancel')" />

    <!-- Modal panel -->
    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 z-10 overflow-hidden">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold text-gray-900">Upload Approved Resume</h3>
          <p class="text-sm text-gray-500 mt-0.5">
            for
            <span class="font-medium text-gray-700">{{ adventurerName }}</span>
          </p>
        </div>
        <button
          class="text-gray-400 hover:text-gray-600 transition-colors"
          @click="$emit('cancel')"
        >
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Body -->
      <div class="px-6 py-5 space-y-4">
        <!-- Upload zone -->
        <div
          class="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors"
          :class="isDragging ? 'border-cosmicPurple bg-cosmicPurple/5' : 'hover:border-cosmicPurple/50'"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
        >
          <div class="p-6 text-center cursor-pointer" @click="triggerFileInput">
            <UploadCloud class="mx-auto mb-3 w-8 h-8 text-gray-400" />
            <p class="text-sm font-medium text-gray-700 mb-1">
              Drag &amp; drop a PDF here, or
              <button
                class="text-cosmicPurple underline underline-offset-2 hover:text-cosmicPurple/80"
                @click.stop="triggerFileInput"
              >
                browse
              </button>
            </p>
            <p class="text-xs text-gray-500">PDF only</p>
            <input
              ref="fileInputRef"
              type="file"
              accept="application/pdf"
              class="hidden"
              @change="onFileSelected"
            />
          </div>

          <!-- Filename convention note -->
          <div
            class="flex gap-2 border-t border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-700 rounded-b-lg"
          >
            <Info class="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <p>
              Name the file using the adventurer's full name, e.g.
              <code class="bg-blue-100 px-1 rounded font-mono">CV_FirstName_LastName.pdf</code>.
              The filename must include the word <strong>CV</strong>.
            </p>
          </div>
        </div>

        <!-- Pending file preview -->
        <div
          v-if="pendingFile"
          class="rounded-lg border border-gray-200 bg-white p-4 space-y-3"
        >
          <div class="flex items-center gap-3">
            <FileText class="w-5 h-5 text-cosmicPurple shrink-0" />
            <span class="text-sm font-medium text-gray-800 truncate">{{ pendingFile.name }}</span>
          </div>
          <div class="space-y-1">
            <label class="text-xs font-medium text-gray-600">Label (optional)</label>
            <input
              v-model="pendingName"
              type="text"
              placeholder="e.g. Main resume, Google version…"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cosmicPurple"
            />
          </div>
          <div v-if="uploadError" class="text-xs text-red-600">{{ uploadError }}</div>
        </div>

        <!-- What will happen note -->
        <div class="rounded-lg bg-lime-50 border border-lime-200 px-4 py-3 text-xs text-lime-800 flex gap-2">
          <CheckCircle2 class="w-3.5 h-3.5 mt-0.5 shrink-0 text-lime-600" />
          <p>
            The uploaded PDF will be <strong>automatically approved</strong> and set as this
            adventurer's <strong>default resume</strong>, then the Resume stage will be marked
            <strong>Passed</strong>.
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2">
        <button
          class="text-sm px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
          :disabled="isSubmitting"
          @click="$emit('cancel')"
        >
          Cancel
        </button>
        <button
          class="text-sm px-4 py-2 rounded-md bg-cosmicPurple text-white font-medium hover:bg-cosmicPurple/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
          :disabled="!pendingFile || isSubmitting"
          @click="confirmUpload"
        >
          <Loader2 v-if="isSubmitting" class="w-3.5 h-3.5 animate-spin" />
          {{ isSubmitting ? submitStatus : 'Upload &amp; Set as Default' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { UploadCloud, FileText, Info, CheckCircle2, Loader2, X } from 'lucide-vue-next'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

const props = defineProps<{
  userId: number
  adventurerName: string
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'cancel'): void
}>()

const fileInputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const pendingFile = ref<File | null>(null)
const pendingName = ref('')
const uploadError = ref('')
const isSubmitting = ref(false)
const submitStatus = ref('')

const triggerFileInput = () => fileInputRef.value?.click()

const onFileSelected = (e: Event) => {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) preparePending(file)
  input.value = ''
}

const onDrop = (e: DragEvent) => {
  isDragging.value = false
  const file = e.dataTransfer?.files?.[0]
  if (!file) return
  if (file.type !== 'application/pdf') {
    uploadError.value = 'Only PDF files are accepted.'
    return
  }
  preparePending(file)
}

const preparePending = (file: File) => {
  if (file.type !== 'application/pdf') {
    uploadError.value = 'Only PDF files are accepted.'
    return
  }
  pendingFile.value = file
  pendingName.value = ''
  uploadError.value = ''
}

const confirmUpload = async () => {
  if (!pendingFile.value) return
  uploadError.value = ''
  isSubmitting.value = true

  try {
    // 1. Upload the resume on behalf of the adventurer
    submitStatus.value = 'Uploading…'
    const formData = new FormData()
    formData.append('file', pendingFile.value)
    formData.append('user_id', String(props.userId))
    if (pendingName.value.trim()) formData.append('name', pendingName.value.trim())

    const uploadRes = await api.post(API_ROUTES.RESUME_FILES_LIST, formData)
    const resumeId: number = uploadRes.data.id

    // 2. Auto-approve the uploaded resume
    submitStatus.value = 'Approving…'
    await api.post(API_ROUTES.RESUME_FILE_APPROVE(resumeId))

    // 3. Set it as the adventurer's default resume
    submitStatus.value = 'Setting as default…'
    await api.post(API_ROUTES.RESUME_FILE_SET_DEFAULT(resumeId))

    emit('success')
  } catch (err: any) {
    const data = err?.response?.data
    uploadError.value =
      data?.file?.[0] ||
      data?.non_field_errors?.[0] ||
      data?.error ||
      data?.detail ||
      'Upload failed. Please try again.'
  } finally {
    isSubmitting.value = false
    submitStatus.value = ''
  }
}
</script>
